//frontend/dispatcher-client/src/hooks/usePersonnelPendingCount.js
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  fetchPersonnelAbsences,
} from "../api/personnelApi";

import {
  getSocket,
} from "../lib/socket";

import {
  getPersonnelCompanyId,
  loadPersonnelResource,
  markPersonnelResourceStale,
  readPersonnelCache,
  subscribePersonnelCache,
} from "../utils/personnelCache";

const PENDING_PARAMS = {
  status:
    "PENDING",
};

const countPending =
  (
    rows
  ) =>
    (
      Array.isArray(
        rows
      )
        ? rows
        : []
    ).filter(
      (
        absence
      ) =>
        String(
          absence
            ?.status ||
            ""
        ).toUpperCase() ===
        "PENDING"
    ).length;

const readPendingCache =
  () =>
    readPersonnelCache(
      "absenceRequests",
      PENDING_PARAMS
    );

export default function usePersonnelPendingCount({
  enabled =
    true,

  listenRealtime =
    true,
} = {}) {
  const [
    pendingCount,
    setPendingCount,
  ] =
    useState(
      () => {
        if (!enabled) {
          return 0;
        }

        return countPending(
          readPendingCache()
            ?.data
        );
      }
    );

  const requestRef =
    useRef(
      0
    );

  const refreshTimerRef =
    useRef(
      null
    );

  const refresh =
    useCallback(
      async ({
        force =
          true,
      } = {}) => {
        if (!enabled) {
          setPendingCount(
            0
          );

          return;
        }

        const requestId =
          requestRef.current +
          1;

        requestRef.current =
          requestId;

        try {
          const rows =
            await loadPersonnelResource({
              resource:
                "absenceRequests",

              params:
                PENDING_PARAMS,

              force,

              loader:
                () =>
                  fetchPersonnelAbsences(
                    PENDING_PARAMS
                  ),
            });

          if (
            requestRef.current !==
            requestId
          ) {
            return;
          }

          setPendingCount(
            countPending(
              rows
            )
          );
        } catch (
          error
        ) {
          console.warn(
            "[PersonnelPendingCount] refresh failed:",
            error
          );
        }
      },
      [
        enabled,
      ]
    );

  const queueRefresh =
    useCallback(
      () => {
        if (!enabled) {
          return;
        }

        if (
          refreshTimerRef.current
        ) {
          window.clearTimeout(
            refreshTimerRef.current
          );
        }

        refreshTimerRef.current =
          window.setTimeout(
            () => {
              refreshTimerRef.current =
                null;

              refresh({
                force:
                  true,
              });
            },
            60
          );
      },
      [
        enabled,
        refresh,
      ]
    );

  /* =======================================================
   * INITIAL CACHE / LOAD
   * ===================================================== */

  useEffect(
    () => {
      if (!enabled) {
        setPendingCount(
          0
        );

        return;
      }

      const cached =
        readPendingCache();

      if (cached) {
        setPendingCount(
          countPending(
            cached.data
          )
        );
      }

      if (
        !cached ||
        cached.isStale
      ) {
        refresh({
          force:
            Boolean(
              cached
            ),
        });
      }
    },
    [
      enabled,
      refresh,
    ]
  );

  /* =======================================================
   * CACHE SYNC
   * ===================================================== */

  useEffect(
    () => {
      if (!enabled) {
        return undefined;
      }

      const unsubscribe =
        subscribePersonnelCache(
          (
            event
          ) => {
            if (
              event.resource !==
              "absenceRequests"
            ) {
              return;
            }

            if (
              event.stale ===
              true
            ) {
              queueRefresh();

              return;
            }

            if (
              String(
                event
                  ?.params
                  ?.status ||
                  ""
              ).toUpperCase() !==
              "PENDING"
            ) {
              return;
            }

            const cached =
              readPendingCache();

            if (!cached) {
              return;
            }

            setPendingCount(
              countPending(
                cached.data
              )
            );
          }
        );

      return () => {
        unsubscribe();
      };
    },
    [
      enabled,
      queueRefresh,
    ]
  );

  /* =======================================================
   * REALTIME
   *
   * Only one consumer needs to own the socket listener.
   * Sidebar uses listenRealtime=true.
   * PersonnelLayout only consumes the shared cache.
   * ===================================================== */

  useEffect(
    () => {
      if (
        !enabled ||
        !listenRealtime
      ) {
        return undefined;
      }

      const socket =
        getSocket();

      const handlePersonnelChanged =
        (
          payload = {}
        ) => {
          const currentCompanyId =
            getPersonnelCompanyId();

          const eventCompanyId =
            Number(
              payload.companyId
            );

          if (
            !currentCompanyId ||
            !Number.isInteger(
              eventCompanyId
            ) ||
            eventCompanyId !==
              currentCompanyId
          ) {
            return;
          }

          const resources =
            Array.isArray(
              payload.resources
            )
              ? payload.resources
              : [];

          const affectsAbsences =
            payload.entity ===
              "ABSENCE" ||
            resources.includes(
              "absenceRequests"
            );

          if (
            !affectsAbsences
          ) {
            return;
          }

          /*
           * Marks every absenceRequests cache variant
           * stale:
           *
           * {}
           * { status: "PENDING" }
           * etc.
           */
          markPersonnelResourceStale(
            "absenceRequests"
          );

          queueRefresh();
        };

      const handleReconnect =
        () => {
          /*
           * Recover any mutation missed while
           * the socket was disconnected.
           */
          queueRefresh();
        };

      socket.on(
        "personnel:changed",
        handlePersonnelChanged
      );

      socket.on(
        "connect",
        handleReconnect
      );

      socket.on(
        "reconnect",
        handleReconnect
      );

      return () => {
        socket.off(
          "personnel:changed",
          handlePersonnelChanged
        );

        socket.off(
          "connect",
          handleReconnect
        );

        socket.off(
          "reconnect",
          handleReconnect
        );
      };
    },
    [
      enabled,
      listenRealtime,
      queueRefresh,
    ]
  );

  useEffect(
    () => () => {
      if (
        refreshTimerRef.current
      ) {
        window.clearTimeout(
          refreshTimerRef.current
        );

        refreshTimerRef.current =
          null;
      }
    },
    []
  );

  return pendingCount;
}